import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../services/api";
import {
  BarChart3,
  Bell,
  ChartNoAxesCombined,
  ChevronDown,
  Code2,
  CreditCard,
  Eye,
  EyeOff,
  Gamepad2,
  Gift,
  Landmark,
  LogOut,
  PanelLeft,
  Play,
  ReceiptText,
  Search,
  Settings,
  ShieldCheck,
  Trophy,
  UserPlus,
  Users
} from "lucide-react";
import BrandLogo from "./BrandLogo";
import { getAdminPermissions, getStoredAdmin } from "../utils/adminAuth";
import { getMarketWindowForNow } from "../utils/marketStatus";

const menuItems = [
  { label: "Dashboard", path: "/admin", icon: BarChart3, permission: "dashboard" },
  { label: "Users", path: "/admin/users", icon: Users, permission: "users" },
  {
    label: "Deposits",
    path: "/admin/deposits",
    icon: Landmark,
    permission: "deposits",
    children: [
      { label: "Deposit Requests", path: "/admin/deposits" },
      { label: "Payment Visits", path: "/admin/payment-visits" },
    ],
  },
  { label: "Payouts", path: "/admin/payouts", icon: Landmark, permission: "withdraws" },
  { label: "P&L Statement", path: "/admin/pnl-statement", icon: ChartNoAxesCombined, permission: "dashboard" },
  { label: "Expenses", path: "/admin/expenses", icon: ReceiptText, permission: "expenses" },
  { label: "Referral Bonus", path: "/admin/referral-bonuses", icon: Gift, permission: "users" },
  { label: "Market Outcome", path: "/admin/results", icon: Trophy, permission: "results" },
  { label: "Auto Result Demo", path: "/admin/auto-results", icon: Search, permission: "results" },
  { label: "Stake Analysis", path: "/admin/stake-analysis", icon: BarChart3, permission: "stake_analysis" },
  {
    label: "Game Management",
    path: "/admin/markets",
    icon: Gamepad2,
    permission: "markets",
    children: [
      { label: "Game Control", path: "/admin/markets" },
      { label: "Bid Revert", path: "/admin/bid-revert" },
      { label: "Game Rates", path: "/admin/game-rates" },
      { label: "Stake Analysis", path: "/admin/stake-analysis" },
      { label: "Chart Upload", path: "/admin/charts" },
    ],
  },
  { label: "Banners", path: "/admin/banners", icon: Gift, permission: "banners" },
  { label: "Payment Gateway", path: "/admin/payment-gateway", icon: CreditCard, permission: "settings" },
  { label: "Send Notifications", path: "/admin/notifications", icon: Bell, permission: "notifications" },
  {
    label: "Agent Program",
    path: "/admin/agents",
    icon: UserPlus,
    permission: "agents",
    children: [
      { label: "Agents", path: "/admin/agents" },
      { label: "Lead CRM", path: "/admin/leads" },
    ],
  },
  { label: "MK Casino", path: "/admin/casino", icon: Play, permission: "casino" },
  { label: "Promotions", path: "/admin/promotions", icon: Gift, permission: "promotions" },
  { label: "Settings", path: "/admin/settings", icon: Settings, permission: "settings" },
  { label: "Coding", path: "/admin/coding", icon: Code2, permission: "settings" },
  { label: "Google Auth", path: "/admin/google-auth", icon: ShieldCheck, permission: "google_auth" },
  { label: "Sub Admins", path: "/admin/sub-admins", icon: ShieldCheck, permission: "sub_admins" },
  {
    label: "Starline",
    path: "/admin/starline/declare",
    icon: BarChart3,
    permission: "results",
    children: [
      { label: "Declare Result", path: "/admin/starline/declare" },
      { label: "Game Control", path: "/admin/starline/control" },
      { label: "Game Rates", path: "/admin/starline/game-rates" },
      { label: "Stake Analysis", path: "/admin/starline/load" },
    ],
  },
];

function AdminSidebar({ isMobile = false, isLaptop = false, isOpen = false, onClose = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = getStoredAdmin();
  const permissions = getAdminPermissions(admin);
  const [collapsed, setCollapsed] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({ deposits: 0, payouts: 0, results: 0 });
  const [profitSummary, setProfitSummary] = useState({
    today_deposit: 0,
    today_withdraw: 0,
    today_profit: 0,
    note: "Work hard",
  });
  const [pageOrder, setPageOrder] = useState([]);
  const [hiddenPages, setHiddenPages] = useState([]);
  const [showHiddenPages, setShowHiddenPages] = useState(false);
  const [menuSearch, setMenuSearch] = useState("");
  const sideStyle = isMobile
    ? { ...sidebar, ...mobileSidebar, transform: isOpen ? "translateX(0)" : "translateX(-105%)" }
    : collapsed ? { ...sidebar, width: "88px", padding: "18px 10px" } : isLaptop ? laptopSidebar : sidebar;

  const isActive = (item) => {
    if (location.pathname === item.path) return true;
    return Array.isArray(item.children) && item.children.some((child) => location.pathname === child.path);
  };

  useEffect(() => {
    let active = true;

    const loadPending = async () => {
      try {
        const [depRes, wdRes] = await Promise.all([
          API.get("/admin/deposits"),
          API.get("/admin/withdraws")
        ]);

        if (!active) return;

        setPendingCounts((current) => ({
          ...current,
          deposits: (Array.isArray(depRes.data) ? depRes.data : []).filter((item) => item.status === "pending").length,
          payouts: (Array.isArray(wdRes.data) ? wdRes.data : []).filter((item) => item.status === "pending").length,
        }));
      } catch (err) {
        console.log(err);
      }
    };

    loadPending();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadPendingResults = async () => {
      try {
        const [marketsRes, resultsRes, settingsRes] = await Promise.all([
          API.get("/admin/markets"),
          API.get("/admin/results"),
          API.get("/admin/settings").catch(() => ({ data: {} })),
        ]);
        if (!active) return;
        const settings = settingsRes.data || {};
        const resetHour = clampInteger(settings.market_result_day_reset_hour, 2, 0, 23);
        const resultDate = getBusinessResultDate(resetHour);
        const rows = Array.isArray(resultsRes.data?.rows) ? resultsRes.data.rows : [];
        const markets = Array.isArray(marketsRes.data)
          ? marketsRes.data.filter((item) => (item.market_type || "normal") !== "starline")
          : [];
        const count = getPendingResultCount(markets, rows, resultDate, {
          delayMinutes: clampInteger(settings.market_pending_result_delay_minutes, 10, 0, 1440),
          dayCutoffHour: clampInteger(settings.market_day_market_cutoff_hour, 18, 0, 23),
        });
        setPendingCounts((current) => ({ ...current, results: count }));
      } catch {
        if (active) setPendingCounts((current) => ({ ...current, results: 0 }));
      }
    };

    loadPendingResults();
    const interval = window.setInterval(loadPendingResults, 60000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadProfitSummary = async () => {
      try {
        const res = await API.get("/admin/sidebar-profit-summary");
        if (!active) return;
        setProfitSummary({
          today_deposit: Number(res.data?.today_deposit || 0),
          today_withdraw: Number(res.data?.today_withdraw || 0),
          today_profit: Number(res.data?.today_profit || 0),
          note: String(res.data?.note || "Work hard"),
        });
      } catch {
        if (active) {
          setProfitSummary((current) => ({ ...current, note: current.note || "Work hard" }));
        }
      }
    };

    loadProfitSummary();
    const interval = window.setInterval(loadProfitSummary, 60000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let active = true;
    API.get("/admin/navigation-order")
      .then((res) => {
        if (!active) return;
        const raw = String(res.data?.admin_page_order || "").trim();
        const next = raw ? raw.split(",").map((item) => item.trim()).filter(Boolean) : [];
        setPageOrder(next);
        setHiddenPages(parseHiddenPages(res.data?.admin_hidden_pages));
      })
      .catch(() => {
        if (active) {
          setPageOrder([]);
          setHiddenPages([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const menuItemsWithCounts = menuItems.filter((item) => permissions[item.permission] !== false).map((item) => {
    if (item.path === "/admin/deposits") {
      return pendingCounts.deposits > 0 ? { ...item, count: pendingCounts.deposits } : item;
    }
    if (item.path === "/admin/payouts") {
      return pendingCounts.payouts > 0 ? { ...item, count: pendingCounts.payouts } : item;
    }
    if (item.path === "/admin/results") {
      return pendingCounts.results > 0 ? { ...item, count: pendingCounts.results } : item;
    }
    return item;
  }).sort((left, right) => {
    if (!pageOrder.length) return 0;
    const leftIndex = pageOrder.indexOf(left.path);
    const rightIndex = pageOrder.indexOf(right.path);
    const safeLeft = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
    const safeRight = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
    if (safeLeft !== safeRight) return safeLeft - safeRight;
    return left.label.localeCompare(right.label);
  });

  const displayedMenuItems = menuItemsWithCounts
    .map((item) => applyHiddenPages(item, hiddenPages, showHiddenPages, location.pathname))
    .filter(Boolean)
    .map((item) => filterMenuItem(item, menuSearch))
    .filter(Boolean);

  return (
    <aside style={sideStyle} className="admin-sidebar">
      <div style={brandRow}>
        <BrandLogo compact={collapsed} size="sm" />
        {!collapsed && <span style={{ flex: 1 }} />}
        <button type="button" style={collapseButton} onClick={() => setCollapsed((c) => !c)}>
          <PanelLeft size={13} color="#94a3b8" />
        </button>
      </div>

      {!collapsed && (
        <div style={searchBox}>
          <Search size={12} color="#94a3b8" />
          <input
            value={menuSearch}
            onChange={(event) => setMenuSearch(event.target.value)}
            placeholder="Search any page..."
            style={searchInput}
          />
        </div>
      )}

      {!collapsed && hiddenPages.length > 0 && (
        <button type="button" style={hiddenToggleButton} onClick={() => setShowHiddenPages((value) => !value)}>
          {showHiddenPages ? <EyeOff size={13} /> : <Eye size={13} />}
          <span>{showHiddenPages ? "Hide unused pages" : `Show hidden pages (${hiddenPages.length})`}</span>
        </button>
      )}

      {!collapsed && <p style={sectionLabel}>MENU</p>}

      <nav>
        {displayedMenuItems.map((item) => {
          const active = isActive(item);
          const searchActive = menuSearch.trim().length > 0;
          const hidden = hiddenPages.includes(item.path);
          const Icon = item.icon;

          return (
            <div key={item.label}>
              <button
                type="button"
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) onClose();
                }}
                style={{ ...menuButton, ...(isLaptop && !collapsed ? laptopMenuButton : {}), ...(collapsed ? collapsedMenuButton : {}), ...(hidden ? hiddenMenuButton : {}), ...(active ? activeMenu : {}) }}
                title={collapsed ? item.label : undefined}
              >
                <span style={menuLeft}>
                  <Icon size={15} />
                  {!collapsed && item.label}
                </span>
                {!collapsed ? (hidden ? <span style={hiddenBadge}>Hidden</span> : item.children ? <ChevronDown size={12} /> : item.count ? <span style={countBadge}>{item.count}</span> : null) : null}
              </button>

              {!collapsed && (active || searchActive) && Array.isArray(item.children) && (
                <div style={subMenu}>
                  {item.children.map((child) => {
                    const childHidden = hiddenPages.includes(child.path);
                    return (
                    <button
                      key={child.path}
                      type="button"
                      onClick={() => {
                        navigate(child.path);
                        if (isMobile) onClose();
                      }}
                      style={{ ...subItem, ...(childHidden ? hiddenSubItem : {}), ...(location.pathname === child.path ? activeSubItem : {}) }}
                    >
                      {child.label}
                      {childHidden ? <span style={hiddenSubBadge}>Hidden</span> : null}
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {!displayedMenuItems.length && !collapsed ? <div style={noResults}>No menu page found</div> : null}
      </nav>

      {!collapsed && <div style={profitBox}>
        <span style={profitLabel}>Today Profit</span>
        <strong style={profitValue}>{formatMoney(profitSummary.today_profit)}</strong>
        <span style={profitDwLine}>
          D {formatMoney(profitSummary.today_deposit)} / W {formatMoney(profitSummary.today_withdraw)}
        </span>
        <span style={profitNote}>{profitSummary.note || "Work hard"}</span>
      </div>}

      {!collapsed && <div style={onlineRow}>
        <span style={onlineDot} />
        <span>System Online</span>
        <span>{admin?.role === "sub_admin" ? "Sub Admin" : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
      </div>}

      <button
        type="button"
        style={{ ...logoutButton, ...(collapsed ? collapsedLogout : {}) }}
        onClick={() => {
          localStorage.removeItem("admin");
          if (isMobile) onClose();
          navigate("/admin-login");
        }}
        title={collapsed ? "Logout" : undefined}
      >
        <LogOut size={13} />
        {!collapsed && "Logout"}
      </button>
    </aside>
  );
}

function filterMenuItem(item, query) {
  const term = String(query || "").trim().toLowerCase();
  if (!term) return item;
  const ownText = `${item.label || ""} ${item.path || ""}`.toLowerCase();
  const childMatches = Array.isArray(item.children)
    ? item.children.filter((child) => `${child.label || ""} ${child.path || ""}`.toLowerCase().includes(term))
    : [];
  if (ownText.includes(term)) return item;
  if (childMatches.length) return { ...item, children: childMatches };
  return null;
}

function parseHiddenPages(value = "") {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function applyHiddenPages(item, hiddenPages = [], showHiddenPages = false, currentPath = "") {
  if (!item) return null;
  const isHidden = hiddenPages.includes(item.path);
  const isCurrent = currentPath === item.path || (Array.isArray(item.children) && item.children.some((child) => currentPath === child.path));
  const children = Array.isArray(item.children)
    ? item.children.filter((child) => showHiddenPages || !hiddenPages.includes(child.path) || currentPath === child.path)
    : undefined;
  if (!showHiddenPages && isHidden && !isCurrent) return null;
  return children ? { ...item, children } : item;
}

function getPendingResultCount(markets = [], rows = [], resultDate, settings = {}) {
  return markets.reduce((count, market) => {
    if (!market || market.is_active === 0) return count;
    const window = getEffectiveMarketWindow(market, resultDate);
    if (window.closed) return count;
    if (isNightMarketHiddenDuringDay(market, resultDate, settings.dayCutoffHour)) return count;
    const declared = getDeclaredSessions(rows, market.id, resultDate);
    const session = !declared.open ? "OPEN" : !declared.close ? "CLOSE" : "";
    if (!session) return count;
    return isResultDue(market, resultDate, session, settings.delayMinutes) ? count + 1 : count;
  }, 0);
}

function getDeclaredSessions(rows = [], marketId, resultDate) {
  const row = rows.find((item) => String(item.market_id) === String(marketId) && String(item.result_date || "").slice(0, 10) === String(resultDate || "").slice(0, 10));
  return {
    open: Boolean(row?.open_pana && row?.open_digit),
    close: Boolean(row?.close_pana && row?.close_digit),
  };
}

function isResultDue(market, resultDate, session = "OPEN", delayMinutes = 10) {
  const window = getEffectiveMarketWindow(market, resultDate);
  const sourceTime = String(session).toUpperCase() === "CLOSE"
    ? (window.close_time || market.close_time)
    : (window.open_time || market.open_time);
  const sessionMinutesRaw = timeStringToMinutes(sourceTime);
  if (sessionMinutesRaw === null) return false;
  const openMinutes = timeStringToMinutes(window.open_time || market.open_time);
  let sessionMinutes = sessionMinutesRaw;
  if (String(session).toUpperCase() === "CLOSE" && openMinutes !== null && sessionMinutes <= openMinutes) sessionMinutes += 1440;

  const resultDayNumber = dateKeyToDayNumber(resultDate);
  const nowParts = getIndiaDateTimeParts();
  const nowDayNumber = dateKeyToDayNumber(nowParts.date);
  if (resultDayNumber === null || nowDayNumber === null) return false;
  const currentMinutes = ((nowDayNumber - resultDayNumber) * 1440) + nowParts.hour * 60 + nowParts.minute;
  return currentMinutes >= sessionMinutes + clampInteger(delayMinutes, 10, 0, 1440);
}

function isNightMarketHiddenDuringDay(market, resultDate, cutoffHour = 18) {
  const nowParts = getIndiaDateTimeParts();
  if (String(resultDate || "").slice(0, 10) !== nowParts.date) return false;
  const currentMinutes = nowParts.hour * 60 + nowParts.minute;
  const cutoffMinutes = clampInteger(cutoffHour, 18, 0, 23) * 60;
  if (currentMinutes >= cutoffMinutes) return false;
  const window = getEffectiveMarketWindow(market, resultDate);
  const closeMinutes = timeStringToMinutes(window.close_time || market.close_time);
  const openMinutes = timeStringToMinutes(window.open_time || market.open_time);
  if (closeMinutes === null) return false;
  if (openMinutes !== null && closeMinutes <= openMinutes) return true;
  return closeMinutes > cutoffMinutes;
}

function getEffectiveMarketWindow(market, resultDate = "") {
  if (!market) return { open_time: "", close_time: "", closed: false };
  const source = resultDate ? new Date(`${resultDate}T12:00:00+05:30`) : new Date();
  return getMarketWindowForNow(market, source);
}

function getBusinessResultDate(resetHour = 2) {
  const nowParts = getIndiaDateTimeParts();
  const base = new Date(Date.UTC(Number(nowParts.year), Number(nowParts.month) - 1, Number(nowParts.day)));
  if (nowParts.hour < clampInteger(resetHour, 2, 0, 23)) base.setUTCDate(base.getUTCDate() - 1);
  return base.toISOString().slice(0, 10);
}

function getIndiaDateTimeParts(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const map = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
  return {
    year: map.year,
    month: map.month,
    day: map.day,
    date: `${map.year}-${map.month}-${map.day}`,
    hour: Number(map.hour) === 24 ? 0 : Number(map.hour || 0),
    minute: Number(map.minute || 0),
  };
}

function dateKeyToDayNumber(value) {
  const match = String(value || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return Math.floor(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 86400000);
}

function timeStringToMinutes(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function clampInteger(value, fallback, min, max) {
  const parsed = Number.parseInt(value, 10);
  const safe = Number.isFinite(parsed) ? parsed : fallback;
  return Math.min(max, Math.max(min, safe));
}

const sidebar = { width: "320px", height: "100vh", background: "#fff", borderRight: "1px solid #e5e7eb", position: "fixed", left: 0, top: 0, padding: "18px 14px", boxSizing: "border-box", overflowY: "auto" };
const laptopSidebar = { ...sidebar, width: "260px", padding: "14px 10px" };
const mobileSidebar = { width: "286px", maxWidth: "86vw", zIndex: 1000, padding: "calc(env(safe-area-inset-top) + 12px) 10px calc(env(safe-area-inset-bottom) + 14px)", transition: "transform 0.28s ease", boxShadow: "0 24px 70px rgba(15,23,42,0.24)", WebkitOverflowScrolling: "touch" };
const brandRow = { display: "flex", alignItems: "center", gap: "12px", height: "56px" };
const collapseButton = { width: "26px", height: "26px", border: 0, background: "transparent", display: "grid", placeItems: "center", cursor: "pointer" };
const searchBox = { marginTop: "12px", height: "56px", display: "flex", alignItems: "center", gap: "10px", padding: "0 16px", border: "1px solid #e5e7eb", color: "#94a3b8", fontSize: "15px", borderRadius: "12px" };
const searchInput = { width: "100%", minWidth: 0, border: 0, outline: 0, background: "transparent", color: "#0f172a", fontSize: "14px", fontWeight: 700 };
const hiddenToggleButton = { width: "100%", minHeight: "40px", marginTop: "10px", border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "12px", fontWeight: 900, cursor: "pointer" };
const sectionLabel = { color: "#94a3b8", fontSize: "13px", margin: "18px 0 8px", fontWeight: 700 };
const menuButton = { width: "100%", height: "52px", border: 0, background: "transparent", color: "#334155", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", fontSize: "15px", cursor: "pointer", borderRadius: "0" };
const laptopMenuButton = { height: "46px", padding: "0 10px", fontSize: "13px" };
const collapsedMenuButton = { justifyContent: "center", padding: 0 };
const activeMenu = { background: "#fff7ed", color: "#92400e", fontWeight: 700 };
const hiddenMenuButton = { opacity: 0.62, background: "#f8fafc" };
const menuLeft = { display: "flex", alignItems: "center", gap: "14px", whiteSpace: "nowrap" };
const countBadge = { background: "#fee2e2", color: "#b91c1c", borderRadius: "7px", padding: "3px 8px", fontSize: "11px", fontWeight: 700 };
const hiddenBadge = { background: "#e2e8f0", color: "#475569", borderRadius: "999px", padding: "3px 8px", fontSize: "10px", fontWeight: 900 };
const subMenu = { marginLeft: "22px", padding: "4px 0 6px 16px", borderLeft: "1px solid #e5e7eb", display: "grid", gap: "2px" };
const subItem = { height: "42px", border: 0, background: "transparent", textAlign: "left", padding: "0 14px", fontSize: "14px", color: "#334155", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" };
const activeSubItem = { background: "#fff7ed", color: "#92400e", fontWeight: 700 };
const hiddenSubItem = { opacity: 0.62 };
const hiddenSubBadge = { fontSize: "9px", fontWeight: 900, color: "#64748b", background: "#e2e8f0", borderRadius: "999px", padding: "2px 6px" };
const noResults = { padding: "18px 12px", color: "#94a3b8", fontSize: "13px", fontWeight: 800 };
const profitBox = { marginTop: "14px", padding: "14px 14px", background: "linear-gradient(135deg,#0f172a,#1d4ed8)", color: "#fff", fontSize: "13px", borderRadius: "16px", display: "grid", gap: "7px", boxShadow: "0 14px 28px rgba(15,23,42,0.16)" };
const profitLabel = { color: "#bfdbfe", fontSize: "11px", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.04em" };
const profitValue = { fontSize: "24px", lineHeight: 1, fontWeight: 900 };
const profitDwLine = { borderTop: "1px solid rgba(255,255,255,0.22)", paddingTop: "8px", color: "#dbeafe", fontSize: "12px", fontWeight: 800 };
const profitNote = { borderTop: "1px solid rgba(255,255,255,0.18)", paddingTop: "7px", color: "#ffffff", fontSize: "12px", fontWeight: 900 };
const onlineRow = { marginTop: "10px", height: "42px", padding: "0 14px", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between", color: "#64748b", fontSize: "13px", borderRadius: "12px" };
const onlineDot = { width: "8px", height: "8px", borderRadius: "50%", background: "#22c55e" };
const logoutButton = { marginTop: "10px", width: "100%", height: "52px", border: "1px solid #fee2e2", background: "#fff5f5", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontSize: "15px", borderRadius: "14px", cursor: "pointer" };
const collapsedLogout = { justifyContent: "center", padding: 0 };

export default AdminSidebar;
